import { useEffect, useMemo, useState, FormEvent } from "react";

export interface StoreOption { 
  value: string | number; 
  label: string 
}

export interface ServiceFormValues {
  code: string;
  name: string;
  branchId: string | number;
  description?: string;
  isActive: boolean;
  imageUrl?: string;
  variant_names?: string[];
}

interface Props {
  storeOptions: StoreOption[];
  onClose: () => void;
  onSubmit: (values: ServiceFormValues) => Promise<boolean> | boolean;
  isSubmitting?: boolean;
  titleText?: string;
  submitText?: string;
  initialValues?: Partial<ServiceFormValues>;
  hideStoreSelect?: boolean;
}

export const ServiceCreateModal: React.FC<Props> = ({
  storeOptions,
  onClose,
  onSubmit,
  isSubmitting = false,
  titleText,
  submitText,
  initialValues,
  hideStoreSelect = false,
}) => {
  const defaultBranchId = useMemo(
    () => (storeOptions.length > 0 ? storeOptions[0].value : ""),
    [storeOptions]
  );

  const [form, setForm] = useState<ServiceFormValues>({
    code: "",
    name: "",
    branchId: defaultBranchId,
    isActive: true,
    variant_names: [], // mặc định mảng rỗng
  });

  // set branchId mặc định khi giá trị hiện tại không nằm trong danh sách
  // (không dùng !form.branchId — 0 là "Dùng chung", một lựa chọn hợp lệ)
  useEffect(() => {
    if (storeOptions.length > 0 && !storeOptions.some((o) => String(o.value) === String(form.branchId))) {
      setForm((p) => ({ ...p, branchId: storeOptions[0].value }));
    }
  }, [form.branchId, storeOptions]);

  // set lại form khi có initialValues
  useEffect(() => {
    if (initialValues) {
      setForm((prev) => ({
        code: initialValues.code ?? prev.code,
        name: initialValues.name ?? prev.name,
        branchId: initialValues.branchId ?? prev.branchId,
        description: initialValues.description ?? prev.description,
        isActive: initialValues.isActive ?? prev.isActive,
        imageUrl: initialValues.imageUrl ?? prev.imageUrl,
        variant_names: initialValues.variant_names ?? prev.variant_names ?? [],
      }));
    }
  }, [initialValues]);

  // handle input change
  const handleChange = <K extends keyof ServiceFormValues>(
    key: K,
    value: ServiceFormValues[K]
  ) => {
    setForm((p) => ({ ...p, [key]: value }));
  };

  // handle variant
  const handleVariantChange = (index: number, value: string) => {
    setForm((prev) => {
      const newVariants = [...(prev.variant_names ?? [])];
      newVariants[index] = value;
      return { ...prev, variant_names: newVariants };
    });
  };

  const addVariant = () => {
    setForm((prev) => ({
      ...prev,
      variant_names: [...(prev.variant_names ?? []), ""],
    }));
  };

  const removeVariant = (index: number) => {
    setForm((prev) => {
      const newVariants = [...(prev.variant_names ?? [])];
      newVariants.splice(index, 1);
      return { ...prev, variant_names: newVariants };
    });
  };

  // submit
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await onSubmit(form);
    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl rounded-3xl bg-white p-8 shadow-2xl">
        <div className="flex items-start justify-between">
          <h2 className="text-2xl font-semibold text-slate-800">
            {titleText ?? "Thêm sản phẩm giặt ủi"}
          </h2>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-full text-xl text-slate-400 hover:bg-slate-100"
          >
            ×
          </button>
        </div>

        <form
          className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2"
          onSubmit={submit}
        >
          {/* Tên sản phẩm */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Tên sản phẩm <span className="text-rose-500">*</span>
            </label>
            <input
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Nhập tên dịch vụ"
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              required
            />
          </div>

          {/* Mã sản phẩm */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Mã sản phẩm <span className="text-rose-500">*</span>
            </label>
            <input
              value={form.code}
              onChange={(e) =>
                handleChange("code", e.target.value.toUpperCase())
              }
              placeholder="GAU_BONG"
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm uppercase shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              required
              maxLength={50}
            />
          </div>


          {/* Đại lý — tạm ẩn: danh mục đang dùng chung cho mọi chi nhánh, branchId lấy từ initialValues (0 = dùng chung).
              Mở lại khi cần tạo sản phẩm riêng theo chi nhánh.
          {hideStoreSelect ? null : (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Đại lý áp dụng <span className="text-rose-500">*</span>
              </label>
              <select
                value={form.branchId}
                onChange={(e) => handleChange("branchId", e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                required
              >
                {storeOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )} */}

          {/* Mô tả */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Mô tả</label>
            <input
              value={form.description ?? ""}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Mô tả ngắn"
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>

          {/* URL ảnh */}
          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-medium text-slate-700">
              URL ảnh đại diện
            </label>
            <input
              value={form.imageUrl ?? ""}
              onChange={(e) => handleChange("imageUrl", e.target.value)}
              placeholder="https://..."
              type="url"
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>

          {/* Buttons */}
          <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-w-[110px] items-center justify-center rounded-full bg-emerald-500 px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-emerald-600"
              disabled={isSubmitting}
            >
              Hủy
            </button>
            <button
              type="submit"
              className="inline-flex min-w-[110px] items-center justify-center rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Đang lưu..." : submitText ?? "Lưu"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceCreateModal;
